"use client"

import { ChevronRight, type LucideIcon } from "lucide-react"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible"
import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/components/ui/sidebar"
import { NavLink, useNavigate, useLocation } from "react-router";
import { useState, useEffect } from "react";

export type NavMainItem = {
  title: string
  url: string
  icon?: LucideIcon
  // 设计图导出的导航图标（1:1）：未选中=石板灰描边（图片 URL，<img> 渲染）
  iconImg?: string
  // 选中态图标：内联 SVG 源码（?raw），fill/stroke 用 currentColor，颜色随 --sidebar-active 按主题切换
  iconImgActive?: string
  isActive?: boolean
  relatedUrls?: string[]
  items?: {
    title: string
    url: string
  }[]
}[];

export function NavMainV2({
  items,
  legacy = false,
}: {
  items: NavMainItem
  legacy?: boolean
}) {
  const params = useLocation();
  const navigate = useNavigate();

  // 根据当前路径找到对应的导航项索引
  const findActiveIndex = () => {
    // -1 = 没匹配上就一项都不高亮；默认 0 会让未登记的子路由(如 AI 導師/個人資料)误高亮第一项 (EN-16)
    let foundIndex = -1;
    items.some((item, idx) => {
      // Check main url
      if (params.pathname === item.url) {
        foundIndex = idx;
        return true;
      }
      // Also check relatedUrls
      if (item.relatedUrls?.includes(params.pathname)) {
        foundIndex = idx;
        return true;
      }
      return false;
    });
    return foundIndex;
  };

  const [activeIndex, setActiveIndex] = useState(findActiveIndex());

  // 监听路由变化，更新高亮状态
  useEffect(() => {
    const newIndex = findActiveIndex();
    setActiveIndex(newIndex);
  }, [params.pathname, items]);

  // 非学生角色（教师等）：还原 github/dev 旧版导航（p-2 / 选中 bg-primary 白字圆角 / lucide 图标）。
  if (legacy) {
    return (
      <SidebarGroup className="p-2">
        <SidebarMenu>
          {items.map((item, index) => (
            <SidebarMenuItem key={item.title}>
              <SidebarMenuButton onClick={() => { setActiveIndex(index); navigate(item.url); }} data-active={activeIndex === index} className="peer data-[active=true]:bg-primary data-[active=true]:text-[#fff] data-[active=true]:rounded-3xl hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:rounded-3xl pl-10 py-6">
                {item.icon && <item.icon />}
                <span className="text-base">{item.title}</span>
              </SidebarMenuButton>
            </SidebarMenuItem>
          ))}
        </SidebarMenu>
      </SidebarGroup>
    )
  }

  return (
    <SidebarGroup className="px-[5%] py-1 xl:px-[6%] xl:py-1 min-[1920px]:px-[35px]! min-[1920px]:py-2!">
      <SidebarMenu className="gap-1.5 xl:gap-2 min-[1920px]:gap-4!">
        {items.map((item, index) => {
          const isActive = activeIndex === index;
          return (
          <SidebarMenuItem key={item.title}>
            {/* 选中项 1536 收到 xl 档(h62/icon24/text16/r18)与 Feedback 同步等比缩, 仅 ≥1920 用 ! 锁 Figma 原值(h83/icon30/text20/r25) */}
            <SidebarMenuButton onClick={() => { setActiveIndex(index); navigate(item.url); }} data-active={isActive} className="peer aspect-[347/83] gap-2.5 pl-[8%] pr-3 py-2 text-[#64748B] rounded-2xl lg:h-12 xl:h-[62px] xl:gap-3 xl:pl-[14%] xl:py-0 xl:rounded-[18px] min-[1920px]:gap-4! min-[1920px]:pl-[18%]! min-[1920px]:h-[83px]! min-[1920px]:rounded-[25px]! [&>svg]:size-5 xl:[&>svg]:size-6 min-[1920px]:[&>svg]:size-[30px]! data-[active=true]:bg-white data-[active=true]:text-[var(--sidebar-active)] data-[active=true]:font-medium data-[active=true]:shadow-sm hover:bg-white/60">
              {item.iconImg
                ? (isActive && item.iconImgActive
                    // 选中：内联 SVG，currentColor 继承按钮文字色（--sidebar-active）；白色符号保持白色
                    ? <span aria-hidden className="inline-flex size-5 shrink-0 xl:size-6 min-[1920px]:size-[30px]! [&>svg]:size-full" dangerouslySetInnerHTML={{ __html: item.iconImgActive }} />
                    : <img src={item.iconImg} alt="" className="size-5 shrink-0 xl:size-6 min-[1920px]:size-[30px]!" />)
                : item.icon && <item.icon />}
              <span className="truncate text-sm xl:text-base min-[1920px]:text-[20px]!">{item.title}</span>
            </SidebarMenuButton>
          </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  )
}

export function NavMain({
  items,
}: {
  items: NavMainItem
}) {
  return (
    <SidebarGroup>
      {/* <SidebarGroupLabel>Platform</SidebarGroupLabel> */}
      <SidebarMenu>
        {items.map((item) => (
          <Collapsible
            key={item.title}
            asChild
            defaultOpen={item.isActive}
            className="group/collapsible"
          >
            <SidebarMenuItem>
              <CollapsibleTrigger asChild>
                <SidebarMenuButton tooltip={item.title}>
                  {item.icon && <item.icon />}
                  <span>{item.title}</span>
                  <ChevronRight className="ml-auto transition-transform duration-200 group-data-[state=open]/collapsible:rotate-90" />
                </SidebarMenuButton>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <SidebarMenuSub>
                  {item.items?.map((subItem) => (
                    <SidebarMenuSubItem key={subItem.title}>
                      <SidebarMenuSubButton asChild>
                        <a href={subItem.url}>
                          <span>{subItem.title}</span>
                        </a>
                      </SidebarMenuSubButton>
                    </SidebarMenuSubItem>
                  ))}
                </SidebarMenuSub>
              </CollapsibleContent>
            </SidebarMenuItem>
          </Collapsible>
        ))}
      </SidebarMenu>
    </SidebarGroup>
  )
}
